import { AlertTriangle, Building2, Mail, Package, Pencil, Trash2 } from 'lucide-react';
import type { Manufacturer } from '../../types';

interface ManufacturerListRowProps {
  manufacturer: Manufacturer;
  onOpen: (manufacturer: Manufacturer) => void;
  onEdit: (manufacturer: Manufacturer) => void;
  onDelete: (id: string) => void;
  onToggleLanguage: (manufacturer: Manufacturer) => void;
}

const emailsOf = (manufacturer: Manufacturer): string[] =>
  Array.isArray(manufacturer.lieferschein_emails) ? manufacturer.lieferschein_emails : [];

/** One manufacturer in the list, including the field-report language switch. */
export function ManufacturerListRow({
  manufacturer,
  onOpen,
  onEdit,
  onDelete,
  onToggleLanguage,
}: ManufacturerListRowProps) {
  const emails = emailsOf(manufacturer);
  const english = manufacturer.field_report_language === 'en';
  const productCount = manufacturer.product_count ?? 0;

  return (
    <div
      className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onOpen(manufacturer)}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="bg-gray-100 rounded-lg p-1.5">
          <Building2 size={16} className="text-gray-500" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{manufacturer.name}</p>
          <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1">
              <Package size={11} />
              {productCount} product{productCount !== 1 ? 's' : ''}
            </span>
            {emails.length > 0 ? (
              <span className="flex items-center gap-1 text-gray-500 truncate">
                <Mail size={11} />
                {emails[0]}
                {emails.length > 1 ? ` +${emails.length - 1} CC` : ''}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle size={11} />
                no email
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onToggleLanguage(manufacturer)}
          title={english ? 'Field report in English' : 'Field report in German'}
          aria-pressed={english}
          className={`h-9 min-w-9 px-2 rounded-lg text-xs font-bold tracking-wide ${
            english
              ? 'bg-brand-600 text-white'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          {english ? 'EN' : 'DE'}
        </button>
        <button
          type="button"
          onClick={() => onEdit(manufacturer)}
          className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
          aria-label="Edit manufacturer"
        >
          <Pencil size={15} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(manufacturer.id)}
          disabled={productCount > 0}
          title={productCount > 0 ? 'Remove all products first' : 'Delete'}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Delete manufacturer"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}
